import { API_BASE } from "../../../lib/api.ts";

/**
 * Client for the AIS agent kill switch (G2 #138): suspend, reinstate and the tenant probation
 * settings. Each write goes through the BFF's service-admin proxy, which enforces the admin role, a
 * reason and a strict step-up (a fresh two-factor code from an operator who has enrolled one) before
 * anything reaches the AIS. The shared `apiFetch` only surfaces `{error:{code}}` bodies, while the AIS
 * answers `{error:"host_mismatch"}`-style strings on a 409, so this client reads both shapes and
 * returns the code, which is what the console needs to say something useful.
 */

/** The three host decisions a reinstatement can carry (identity-core HOST_DECISIONS). */
export type HostDecision = "matched" | "accept_new_host" | "accept_unbound";

/** What `/token` recorded while the agent was suspended (a SHA-256 prefix, never the fingerprint). */
export interface HostObservation {
  readonly fingerprint_hash: string | null;
  readonly matches: boolean | null;
  readonly observed_at: string;
}

/** One entry of an incident's timeline. */
export interface TimelineEntry {
  readonly seq: number;
  readonly at: string;
  readonly type: string;
  readonly actor: string;
  readonly actor_role: string;
  readonly reason: string;
  readonly detail?: Readonly<Record<string, unknown>>;
}

export interface Incident {
  readonly incident_id: string;
  readonly state: "open" | "probation" | "closed";
  readonly closed_outcome: "completed" | "revoked" | "deleted" | null;
  readonly timeline: readonly TimelineEntry[];
}

export interface ProbationSettings {
  readonly probation_seconds: number;
  readonly host_observation_max_age_s: number;
  readonly version?: number;
}

/** `GET /admin/api/agents/:ref/incidents`. */
export interface IncidentsResponse {
  readonly agent_ref: string;
  readonly status: string;
  readonly incident_id: string | null;
  readonly last_host_observation: HostObservation | null;
  readonly probation_until: string | null;
  readonly settings: ProbationSettings;
  readonly incidents: readonly Incident[];
}

export type LifecycleResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

/** The query-key root every lifecycle read lives under, so one invalidation refreshes them all. */
export const LIFECYCLE_QUERY_ROOT = ["service-admin", "ais"] as const;

/** A fresh idempotency key for one modal; a retry of the same submission reuses it. */
export function newOpId(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return `op_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function codeOf(parsed: unknown, status: number): string {
  if (typeof parsed === "object" && parsed !== null) {
    const error = (parsed as { error?: unknown }).error;
    if (typeof error === "string") return error;
    if (typeof error === "object" && error !== null && typeof (error as { code?: unknown }).code === "string") {
      return (error as { code: string }).code;
    }
  }
  return `http_${status}`;
}

/** Send one lifecycle write. Never throws for an HTTP answer; a network failure resolves `network`. */
export async function lifecycleWrite<T>(
  method: "POST" | "PUT",
  subpath: string,
  body: Readonly<Record<string, unknown>>,
  signal?: AbortSignal,
): Promise<LifecycleResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/service-admin/ais/${subpath}`, {
      method,
      headers: { accept: "application/json", "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    return { ok: false, status: 0, code: "network" };
  }
  const parsed: unknown = await response.json().catch(() => null);
  if (response.ok) return { ok: true, body: parsed as T };
  return { ok: false, status: response.status, code: codeOf(parsed, response.status) };
}

export function suspendPath(agentRef: string): string {
  return `agents/${encodeURIComponent(agentRef)}/suspend`;
}
export function reinstatePath(agentRef: string): string {
  return `agents/${encodeURIComponent(agentRef)}/reinstate`;
}
export function incidentsPath(agentRef: string): string {
  return `agents/${encodeURIComponent(agentRef)}/incidents`;
}
export const PROBATION_SETTINGS_PATH = "tenant-settings/probation";

/** Plain-language text for a refusal code. Anything unknown gets a generic line, never a raw code. */
export function lifecycleMessage(code: string): string {
  switch (code) {
    case "mfa_required":
      return "Enter a current two-factor code to continue.";
    case "mfa_enrollment_required":
      return "Set up two-factor authentication on your account first. This action needs a second factor.";
    case "invalid_request":
      return "Add a reason of 1 to 256 characters.";
    case "forbidden":
      return "Only a team administrator can do this.";
    case "host_observation_required":
      return "The AIS has no recent host observation for this agent. Have the agent call in from its host, then try again.";
    case "host_mismatch":
      return "The agent was last seen on a different host. Accept the new host, or wait until it calls in from the enrolled one.";
    case "host_binding_present":
      return "This agent has an enrolled host, so it cannot be reinstated as unbound.";
    case "principal_not_active":
      return "The agent's owner has been deprovisioned, so it cannot be reinstated.";
    case "already_suspended":
    case "not_suspended":
    case "not_suspendable":
    case "incident_mismatch":
    case "version_conflict":
    case "op_id_conflict":
      return "The agent changed while this dialog was open. Close it and review the current state.";
    case "incident_timeline_full":
      return "This incident has reached its timeline limit. Delete and re-enrol the agent, or revoke it.";
    case "service_auth_failed":
      return "The two-factor confirmation could not be passed to the AIS. Try again with a fresh code.";
    case "tenant_required":
    case "not_found":
      return "That agent is not in your team.";
    case "network":
      return "The request could not be sent. Check your connection and try again.";
    default:
      return "The action could not be completed. Nothing was changed.";
  }
}

/** How a host observation reads to an operator, given the freshness window in seconds. */
export function describeObservation(observation: HostObservation | null, maxAgeS: number, nowMs: number): {
  readonly kind: "none" | "stale" | "matches" | "differs" | "unknown";
  readonly text: string;
} {
  if (observation === null) {
    return { kind: "none", text: "No host observation yet. The agent has not called in since it was suspended." };
  }
  const at = Date.parse(observation.observed_at);
  if (!Number.isFinite(at) || nowMs - at > maxAgeS * 1000) {
    return { kind: "stale", text: `The last host observation is older than ${Math.round(maxAgeS / 60)} minutes. The agent must call in again.` };
  }
  if (observation.matches === true) return { kind: "matches", text: "The agent last called in from its enrolled host." };
  if (observation.matches === false) return { kind: "differs", text: "The agent last called in from a different host than the one it enrolled on." };
  return { kind: "unknown", text: "The agent called in, but there is no enrolled host to compare it with." };
}
